// FROM: https://github.com/modusign/modusign-client/blob/b6c3d577fdd8384ea2f73c5ab605ead1afc782d1/app/src/components/ParticipantFormPhoneField/index.tsx

import {
  ArrowIcon,
  Box,
  ClearIcon,
  Flex,
  Input,
  Text,
  TextButton,
  Tooltip,
  ValidationMessage,
} from '@modusign/design-system';
import { SignerToReference, SigningMethod } from '@modusign/react-signing/types';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useMount } from 'react-use';
import isMobilePhone from 'validator/lib/isMobilePhone';

import SigningMethodSelect from '@app/features/apply-react-signing/components/SignerCardUI/SignerFormContactAndMethodField/SigningMethodSelect';
import { duplicatedContactInfoValidator } from '@app/features/apply-react-signing/libs/validations/checkDuplicatedContactInfo';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { getFormattedShortPhoneNumber } from '@app/libs/inputFormat';

interface SignerFormPhoneFieldProps {
  signerId: string;
  order: number;
  required?: boolean;
  onSigningMethodChange: (method: SigningMethod) => void;
  onContactValueReset: () => void;
}

// a 태그로 사용시 서명자 카드의 드래그 이벤트와 겹쳐 이동이 되지않는 문제 발생
// onClick으로 js 통해서 이동하도록 처리
function goToHelpCenter() {
  window.open(
    'https://support.modusign.co.kr/hc/ko/articles/360008205834',
    '_blank',
    'noopener noreferrer'
  );
}

const PHONE_NUMBER_IDENTIFIER = '010';
const PHONE_NUMBER_REST_LENGTH = 8;

type PhoneNumber = `010${string}`;

function phoneNumberValidator(value: PhoneNumber) {
  const v = value?.trim().replace(/\D/g, '') as PhoneNumber;

  if (v.length === PHONE_NUMBER_IDENTIFIER.length) {
    return '번호를 입력해주세요.';
  }

  if (v.length < PHONE_NUMBER_REST_LENGTH + PHONE_NUMBER_IDENTIFIER.length) {
    return '8자리 번호를 입력해주세요.';
  }

  if (!isMobilePhone(v, 'ko-KR')) {
    return '올바르지 않은 형식입니다.';
  }

  return true;
}

function SignerFormPhoneField({
  signerId,
  order,
  required = true,
  onSigningMethodChange,
  onContactValueReset,
}: SignerFormPhoneFieldProps) {
  const isMobileWidth = useResponsiveValue([true, false, false]);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { control, errors, getValues, trigger } =
    useFormContext<Record<string, SignerToReference>>();
  const {
    field: { value, ref, onChange },
  } = useController({
    control,
    name: `${signerId}.contact`,
    defaultValue: '',
    rules: {
      validate: {
        isInvalid: (value) => {
          if (!required && !value) {
            return true;
          }
          return phoneNumberValidator(value);
        },
        isDuplicated: (targetContact) =>
          duplicatedContactInfoValidator({
            signers: getValues(),
            signerId,
            order,
            targetContact,
            errors,
          }),
      },
      setValueAs: (v: string) => v.replace(/\D/g, ''),
    },
  });

  const error = errors[signerId]?.contact?.message || '';

  const debounceTrigger = debounce(() => {
    trigger();
  }, 600);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(`${PHONE_NUMBER_IDENTIFIER}${e.target.value}`);
      // 현재 입력 중인 인풋 외 다른 인풋도 같이 검증하여 contact info의 중복체크를 합니다.
      debounceTrigger();
    },
    [debounceTrigger, onChange]
  );

  useMount(() => {
    if (ref.current) {
      ref.current.focus();
    }
  });

  return (
    <Flex mb={6}>
      <Box width="80px">
        <SigningMethodSelect value="KAKAO" onSelect={onSigningMethodChange} />
      </Box>
      <Box width="100%">
        <Tooltip
          content={
            <>
              카카오톡 이용자가 아닌 경우 문자로 전송됩니다.
              <br />
              <TextButton mode="night" type="button" onClick={goToHelpCenter}>
                자세히 <ArrowIcon direction="right" ml="2px" />
              </TextButton>
            </>
          }
          isOpen={isTooltipOpen && !isMobileWidth}
          placement="right"
        >
          <Box sx={{ position: 'relative' }} width="100%">
            <Text sx={{ left: 4, position: 'absolute', top: 3 }}>{PHONE_NUMBER_IDENTIFIER}-</Text>
            <Input
              isInvalid={!!error}
              maxLength={9}
              name="smartPhone"
              placeholder="8자리 '-' 없이 입력"
              ref={ref}
              sx={{ paddingLeft: '44px' }}
              type="tel"
              value={getFormattedShortPhoneNumber(
                (value as string).replace(new RegExp(PHONE_NUMBER_IDENTIFIER), '')
              )}
              onBlur={() => setIsTooltipOpen(false)}
              onChange={handleChange}
              onFocus={() => setIsTooltipOpen(true)}
            />
            {value && (
              <Box
                aria-label="clear"
                as="button"
                sx={{
                  position: 'absolute',
                  right: 3,
                  top: 2,
                }}
                onClick={onContactValueReset}
              >
                <ClearIcon />
              </Box>
            )}
          </Box>
        </Tooltip>
        {error && (
          <ValidationMessage fontSize="sm" fontWeight="regular">
            {error}
          </ValidationMessage>
        )}
      </Box>
    </Flex>
  );
}

export default SignerFormPhoneField;
